import { ReactNode, useCallback, useState } from "react"
import { DateTime } from "luxon"
import { enqueueSnackbar } from "notistack"
import { MenuItem } from "@mui/material"
import { MenuPlaningQuery, deleteMenu, switchMenuStatus } from "../menuPlanningApi"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DayMenuDialog } from "../components/dayMenuDialog/DayMenuDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useDateTime } from "../../../../localization/useDateTime"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

type ActionItemData = { id: number; publishDate: string }

type UseMenuPlanningItemActionsReturnValue = [
    (item: ActionItemData, closeMenu?: () => void) => ReactNode[],
    ReactNode[],
]

export const useMenuPlanningItemActions = (): UseMenuPlanningItemActionsReturnValue => {
    const dateTimeFactory = useDateTime()

    const today = dateTimeFactory.now()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)
    const [dayMenuModalState, setDayMenuModalState] = useState<{
        show: boolean
        menuId: null | number
        dateInfo: DateTime
    }>({
        show: false,
        menuId: null,
        dateInfo: today,
    })

    const { mutate: callDeleteMenu } = useApiMutation(deleteMenu, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MenuPlaningQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { mutate: callSwitchMenuStatus } = useApiMutation(switchMenuStatus, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MenuPlaningQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteMenu({ menuId: confirmModalState.itemId })
        } else {
            throw new Error("Menu id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteMenu])

    const getActions = (item: ActionItemData, closeMenu?: () => void): JSX.Element[] => [
        <LocalizedStrict id="merchant-menu-planning-table-menu-item-edit">
            <MenuItem
                onClick={() => {
                    setDayMenuModalState({
                        show: true,
                        menuId: item.id,
                        dateInfo: dateTimeFactory.fromApi(item.publishDate),
                    })
                    closeMenu && closeMenu()
                }}
                key={`edit-${item.id}`}
            >
                Edit
            </MenuItem>
        </LocalizedStrict>,
        <LocalizedStrict id="merchant-menu-planning-table-menu-item-set-inactive">
            <MenuItem
                onClick={() => {
                    callSwitchMenuStatus({ menuId: item.id })
                    closeMenu && closeMenu()
                }}
                key={`set-status-${item.id}`}
            >
                Set status
            </MenuItem>
        </LocalizedStrict>,
        <LocalizedStrict id="merchant-menu-planning-table-menu-item-delete">
            <MenuItem
                onClick={() => {
                    const formattedDate = DateTime.fromISO(item.publishDate).toFormat("cccc - dd/MM")

                    setConfirmModalState({
                        showConfirmModal: true,
                        confirmModalText: `${formattedDate}`,
                        itemId: item.id,
                    })
                    closeMenu && closeMenu()
                }}
                key={`delete-${item.id}`}
            >
                Delete
            </MenuItem>
        </LocalizedStrict>,
    ]

    const restElements = [
        confirmModalState.showConfirmModal ? (
            <LocalizedStrict id="merchant-menu-planning-confirm-delete-shop-dialog" attrs={{ typeOfEntity: true }}>
                <DeleteConfirmDialog
                    typeOfEntity="menu"
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    open={confirmModalState.showConfirmModal}
                    onClose={() => setConfirmModalState(defaultModalState)}
                />
            </LocalizedStrict>
        ) : null,
        dayMenuModalState.show ? (
            <DayMenuDialog
                open={dayMenuModalState.show}
                menuId={dayMenuModalState.menuId}
                dateInfo={dayMenuModalState.dateInfo}
                onClose={() =>
                    setDayMenuModalState({
                        show: false,
                        menuId: null,
                        dateInfo: today,
                    })
                }
            />
        ) : null,
    ]

    return [getActions, restElements]
}
